import React, { useState } from 'react';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useCurrency } from '../hooks/useCurrency';
import { getBasePriceUSD } from '../utils/pricing';
import { stripePromise } from '../utils/stripe';

interface PaymentFormProps {
  trees: number;
  packageType: string;
  // method lets the parent show the right confirmation: card payments are
  // final, bank transfers still need 24-48h manual verification.
  onSuccess: (method: 'card' | 'transfer') => void;
  onCancel: () => void;
}

type PaymentMethod = 'stripe' | 'bancolombia';

const PaymentForm: React.FC<PaymentFormProps> = ({ trees, packageType, onSuccess, onCancel }) => {
  const { pricing, calculatePrice } = useCurrency();
  const stripe = useStripe();
  const elements = useElements();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>('stripe');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  // Manual payment specific states
  const [referenceNumber, setReferenceNumber] = useState('');
  const [evidenceFiles, setEvidenceFiles] = useState<FileList | null>(null);

  // Calculate pricing (shared module — matches backend tiers/discounts)
  const totalUSD = getBasePriceUSD(trees, packageType);
  const currency = pricing?.currency || 'USD';
  const formattedTotal = calculatePrice(totalUSD);

  const handleInputChange = (field: string, value: string) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleManualPaymentSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!customerInfo.name || !customerInfo.email) {
      setError('Por favor completa todos los campos requeridos');
      return;
    }
    
    if (!evidenceFiles || evidenceFiles.length === 0) {
      setError('Por favor sube la evidencia del pago');
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('trees', trees.toString());
      formData.append('packageType', packageType);
      formData.append('currency', pricing?.currency || 'COP');
      formData.append('customerEmail', customerInfo.email);
      formData.append('customerName', customerInfo.name);
      formData.append('customerPhone', customerInfo.phone);
      formData.append('paymentMethod', 'manual_bancolombia');
      formData.append('bankName', 'bancolombia');
      formData.append('referenceNumber', referenceNumber);
      
      // Add evidence files
      for (let i = 0; i < evidenceFiles.length; i++) {
        formData.append('evidenceFiles', evidenceFiles[i]);
      }
      
      const response = await fetch('/api/manual-payments/create-order', {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create manual payment order');
      }
      
      onSuccess('transfer');

    } catch (err) {
      console.error('Manual payment error:', err);
      setError(err instanceof Error ? err.message : 'El pago falló. Por favor inténtalo de nuevo.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    if (selectedPaymentMethod === 'bancolombia') {
      return handleManualPaymentSubmit(event);
    }
    event.preventDefault();
    
    if (!stripe || !elements) {
      setError('Stripe aún no se ha cargado. Por favor inténtalo de nuevo.');
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setError('Elemento de tarjeta no encontrado. Por favor recarga la página.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Create payment intent on the backend
      const paymentIntentResponse = await fetch('/api/pricing/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          trees,
          packageType,
          currency: currency,
          customerEmail: customerInfo.email,
          customerName: customerInfo.name,
          locale: 'es'
        }),
      });

      const { client_secret, error: intentError } = await paymentIntentResponse.json();

      if (intentError || !client_secret) {
        throw new Error(intentError || 'Failed to create payment intent');
      }

      // Confirm payment with Stripe
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: customerInfo.name,
            email: customerInfo.email
          },
        }
      });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (paymentIntent?.status === 'succeeded') {
        onSuccess('card');
      } else if (paymentIntent?.status === 'processing') {
        setError('Tu pago está en proceso. Te enviaremos un correo cuando se confirme — no vuelvas a pagar.');
      } else {
        setError('El pago no se completó. Por favor inténtalo de nuevo.');
      }

    } catch (err) {
      console.error('Payment error:', err);
      setError(err instanceof Error ? err.message : 'El pago falló. Por favor inténtalo de nuevo.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Stripe Elements styling for separate fields
  const elementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#333',
        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
        iconColor: '#228B22'
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: '600px', margin: '0 auto' }}>
      {/* Order Summary */}
      <div style={{
        background: '#f8f9fa',
        padding: '1.5rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        border: '2px solid #e9ecef'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#228B22', fontSize: '1.2rem' }}>
          Resumen del Pedido
        </h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span>{trees} {trees === 1 ? 'Árbol' : 'Árboles'} ({packageType})</span>
          <span style={{ fontWeight: 'bold', color: '#228B22' }}>{formattedTotal}</span>
        </div>
        <div style={{ fontSize: '0.9rem', color: '#666' }}>
          Ayuda a restaurar los bosques de Colombia con árboles nativos
        </div>
      </div>

      {/* Payment Method Selection */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>
          Seleccionar Método de Pago
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div
            onClick={() => setSelectedPaymentMethod('stripe')}
            style={{
              padding: '1rem',
              border: `2px solid ${selectedPaymentMethod === 'stripe' ? '#228B22' : '#e0e0e0'}`,
              borderRadius: '12px',
              cursor: 'pointer',
              textAlign: 'center',
              background: selectedPaymentMethod === 'stripe' ? '#f0f8f0' : 'white',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>💳</div>
            <div style={{ fontWeight: '600', color: selectedPaymentMethod === 'stripe' ? '#228B22' : '#333' }}>
              Tarjeta de Crédito
            </div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
              Visa, Mastercard, etc.
            </div>
          </div>
          
          <div
            onClick={() => setSelectedPaymentMethod('bancolombia')}
            style={{
              padding: '1rem',
              border: `2px solid ${selectedPaymentMethod === 'bancolombia' ? '#228B22' : '#e0e0e0'}`,
              borderRadius: '12px',
              cursor: 'pointer',
              textAlign: 'center',
              background: selectedPaymentMethod === 'bancolombia' ? '#f0f8f0' : 'white',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🇨🇴</div>
            <div style={{ fontWeight: '600', color: selectedPaymentMethod === 'bancolombia' ? '#228B22' : '#333' }}>
              Bancolombia
            </div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
              Transferencia bancaria
            </div>
          </div>
        </div>
      </div>

      {/* Customer Information */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>
          Información del Cliente
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: selectedPaymentMethod === 'bancolombia' ? '1fr 1fr 1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
              Nombre Completo
            </label>
            <input
              type="text"
              value={customerInfo.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#228B22'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
              Correo Electrónico
            </label>
            <input
              type="email"
              value={customerInfo.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#228B22'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>
          {selectedPaymentMethod === 'bancolombia' && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                Número de Teléfono
              </label>
              <input
                type="tel"
                value={customerInfo.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+57 300 123 4567"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#228B22'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Bancolombia Payment Instructions */}
      {selectedPaymentMethod === 'bancolombia' && (
        <div style={{
          background: '#e3f2fd',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '2px solid #2196f3'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1976d2', fontSize: '1.1rem' }}>
            🇨🇴 Opciones de Pago Colombia
          </h3>

          <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontWeight: 600, color: '#b8860b' }}>Opción 1 - Bancolombia:</div>
                <div>Cuenta de Ahorros: <strong>43000003138</strong></div>
              </div>
              <div style={{ borderTop: '1px solid #eee', paddingTop: '0.75rem' }}>
                <div style={{ fontWeight: 600, color: '#b8860b' }}>Opción 2 - Nequi/Daviplata:</div>
                <div>Llave BRE-B: <strong>0091585989</strong></div>
              </div>
            </div>
          </div>

          <div style={{ fontSize: '0.9rem', color: '#1976d2' }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>Monto a Transferir:</strong> {formattedTotal}
            </div>
            <ol style={{ paddingLeft: '1.5rem', margin: '0.5rem 0' }}>
              <li>Transfiere el monto exacto a la cuenta de arriba</li>
              <li>Guarda el comprobante de la transferencia</li>
              <li>Sube una foto clara del comprobante abajo</li>
              <li>Incluye el número de referencia de la transacción</li>
            </ol>
          </div>
        </div>
      )}

      {/* Bancolombia Form Fields */}
      {selectedPaymentMethod === 'bancolombia' && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>
            Comprobante de Pago
          </h3>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
              Número de Referencia de la Transacción
            </label>
            <input
              type="text"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder="123456789"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#228B22'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
              Subir Comprobante de Pago (Máx. 3 archivos, 5MB cada uno)
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setEvidenceFiles(e.target.files)}
              required={selectedPaymentMethod === 'bancolombia'}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px dashed #228B22',
                borderRadius: '8px',
                fontSize: '16px',
                background: '#f8f9fa',
                cursor: 'pointer'
              }}
            />
            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
              Formatos aceptados: JPG, PNG, GIF. Máximo 3 archivos.
            </div>
          </div>
        </div>
      )}

      {/* Stripe Payment Information */}
      {selectedPaymentMethod === 'stripe' && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>
            Información de Pago
          </h3>
        
        {/* Card Number */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
            Número de Tarjeta
          </label>
          <div style={{
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            padding: '12px',
            background: 'white',
            transition: 'border-color 0.2s'
          }}>
            <CardNumberElement options={elementOptions} />
          </div>
        </div>
        
        {/* Expiry and CVC */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
              MM / AA
            </label>
            <div style={{
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              padding: '12px',
              background: 'white',
              transition: 'border-color 0.2s'
            }}>
              <CardExpiryElement options={elementOptions} />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
              CVC
            </label>
            <div style={{
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              padding: '12px',
              background: 'white',
              transition: 'border-color 0.2s'
            }}>
              <CardCvcElement options={elementOptions} />
            </div>
          </div>
        </div>
        
        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem', display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '0.5rem' }}>🔒</span>
          Tu información de pago está encriptada y es segura
        </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid #ef5350'
        }}>
          {error}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          style={{
            flex: 1,
            padding: '14px',
            background: '#666',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            opacity: isProcessing ? 0.6 : 1,
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => !isProcessing && (e.currentTarget.style.backgroundColor = '#333')}
          onMouseLeave={(e) => !isProcessing && (e.currentTarget.style.backgroundColor = '#666')}
        >
          Cancelar
        </button>
        
        <button
          type="submit"
          disabled={
            isProcessing || 
            !customerInfo.name || 
            !customerInfo.email || 
            (selectedPaymentMethod === 'stripe' && !stripe) ||
            (selectedPaymentMethod === 'bancolombia' && (!evidenceFiles || evidenceFiles.length === 0))
          }
          style={{
            flex: 2,
            padding: '14px',
            background: selectedPaymentMethod === 'bancolombia' ? 'linear-gradient(135deg, #1976d2, #2196f3)' : 'linear-gradient(135deg, #228B22, #32CD32)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: (
              isProcessing || 
              !customerInfo.name || 
              !customerInfo.email || 
              (selectedPaymentMethod === 'stripe' && !stripe) ||
              (selectedPaymentMethod === 'bancolombia' && (!evidenceFiles || evidenceFiles.length === 0))
            ) ? 'not-allowed' : 'pointer',
            opacity: (
              isProcessing || 
              !customerInfo.name || 
              !customerInfo.email || 
              (selectedPaymentMethod === 'stripe' && !stripe) ||
              (selectedPaymentMethod === 'bancolombia' && (!evidenceFiles || evidenceFiles.length === 0))
            ) ? 0.6 : 1,
            transition: 'opacity 0.2s'
          }}
        >
          {isProcessing ? (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{
                width: '16px',
                height: '16px',
                border: '2px solid transparent',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: '8px'
              }}></span>
              {selectedPaymentMethod === 'bancolombia' ? 'Enviando...' : 'Procesando...'}
            </span>
          ) : (
            selectedPaymentMethod === 'bancolombia'
              ? 'Enviar Comprobante de Pago'
              : `Pagar ${formattedTotal}`
          )}
        </button>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </form>
  );
};

interface CustomTreePaymentFormProps {
  trees: number;
  packageType: string;
  onSuccess: (method: 'card' | 'transfer') => void;
  onCancel: () => void;
}

const CustomTreePaymentForm: React.FC<CustomTreePaymentFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} />
    </Elements>
  );
};

export default CustomTreePaymentForm;